import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import api from '../../services/api';
import { getAuthenticatedMediaUrl } from '../../utils/mediaUtils';
import '../../App.css';

/**
 * Playback UI for both audio and video
 * Shows video player or audio player with equalizer animation
 * Includes collapsible transcription text display at the bottom
 */
function PlaybackSection({
  type,
  url,
  thumbnailUrl,
  onAudioStateChange,
  isPlaying,
  reflectionId,
  onViewStatusChange,
  transcriptionText,
  transcriptionStatus,
  transcriptionPreference,
  onNavigateToSettings
}) {
  const [showTranscription, setShowTranscription] = useState(false);
  const hasTranscription = transcriptionStatus === 'completed' && transcriptionText;
  // Append authentication token to media URL
  const authenticatedUrl = useMemo(() => getAuthenticatedMediaUrl(url), [url]);
  const authenticatedThumbnailUrl = useMemo(
    () => thumbnailUrl ? getAuthenticatedMediaUrl(thumbnailUrl) : null,
    [thumbnailUrl]
  );

  // Create refs for media elements
  const mediaRef = useRef(null);
  const sessionIdRef = useRef(null);
  const isAutoplayingRef = useRef(false); // Track if currently autoplaying for buffering

  // Generate session ID on mount
  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = crypto.randomUUID();
    }
  }, []);

  // Track playback events
  const trackPlayback = useCallback(async (eventType, playbackPosition = null) => {
    if (!reflectionId || !sessionIdRef.current) return;

    // Don't track if this is an autoplay for buffering (not user-initiated)
    if (isAutoplayingRef.current && eventType === 'play') {
      console.log(`🔇 Skipping tracking for autoplay buffering (${type})`);
      return;
    }

    try {
      await api.post('/reflection-views/playback', {
        reflectionId,
        mediaType: type,
        eventType,
        playbackPosition,
        sessionId: sessionIdRef.current
      });

      // Notify parent if view status changed (only on 'play' events)
      if (eventType === 'play' && onViewStatusChange) {
        onViewStatusChange();
      }
    } catch (error) {
      console.error('Error tracking playback:', error);
    }
  }, [reflectionId, type, onViewStatusChange]);

  // Force load the media as soon as the component mounts
  useEffect(() => {
    if (mediaRef.current && authenticatedUrl) {
      console.log(`🔄 Forcing ${type} preload:`, authenticatedUrl);

      // Strategy: Autoplay muted to force buffering, then pause immediately
      // This tricks the browser into thinking the user wants to play it
      const media = mediaRef.current;

      // Mark as autoplaying so we don't track this as a view
      isAutoplayingRef.current = true;

      // Mute first (allows autoplay without user interaction)
      media.muted = true;

      // Start playing to force buffering
      const playPromise = media.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Successfully started playing - pause it and unmute
            console.log(`✅ ${type} buffering started via autoplay`);
            media.pause();
            media.muted = false;
            media.currentTime = 0; // Reset to beginning
            isAutoplayingRef.current = false; // Done autoplaying
          })
          .catch((error) => {
            // Autoplay failed (unlikely with muted), fall back to load()
            console.warn(`⚠️ ${type} autoplay failed, using load():`, error.message);
            media.muted = false;
            media.load();
            isAutoplayingRef.current = false; // Done autoplaying
          });
      }
    }
  }, [authenticatedUrl, type]);
  const handleMediaError = (e, mediaType) => {
    console.error(`❌ ${mediaType} failed to load`);
    console.error('Original URL:', url);
    console.error('Authenticated URL:', authenticatedUrl);
    console.error('Error code:', e.target.error?.code);
    console.error('Error message:', e.target.error?.message);
    console.error('Network state:', e.target.networkState);
    console.error('Ready state:', e.target.readyState);
    console.error('Current src:', e.target.src);

    // Try to fetch the URL directly to get HTTP status
    fetch(authenticatedUrl, { method: 'HEAD' })
      .then(response => {
        console.error('HTTP Status:', response.status, response.statusText);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      })
      .catch(err => {
        console.error('Fetch error:', err.message);
      });
  };

  // Transcription display component
  const TranscriptionDisplay = () => {
    if (!hasTranscription) {
      if (transcriptionPreference === 'never' && onNavigateToSettings) {
        return (
          <p className="transcription-disabled-note" style={{
            marginTop: '1rem',
            color: 'var(--color-text-secondary)',
            fontSize: '0.9rem'
          }}>
            Transcription disabled.{' '}
            <a
              href="?page=settings&tab=preferences"
              onClick={(e) => { e.preventDefault(); onNavigateToSettings(); }}
            >
              Go to settings
            </a>{' '}
            and turn on AI to enable transcriptions.
          </p>
        );
      }
      return null;
    }

    return (
      <div style={{ marginTop: '1rem' }}>
        <button
          className="studio-transcription-toggle"
          onClick={() => setShowTranscription(!showTranscription)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            padding: '0.25rem 0',
            fontSize: '0.9rem',
            textDecoration: 'underline'
          }}
        >
          {showTranscription ? 'Hide transcription' : 'Show transcription'}
        </button>
        {showTranscription && (
          <div className="studio-transcription-text" style={{
            marginTop: '0.5rem',
            backgroundColor: 'var(--color-background-secondary)',
            padding: '1rem',
            borderRadius: '0.5rem',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflowY: 'auto',
            fontSize: '0.9rem'
          }}>
            {transcriptionText}
          </div>
        )}
      </div>
    );
  };

  if (type === 'video') {
    return (
      <div>
        <video
          ref={mediaRef}
          src={authenticatedUrl}
          poster={authenticatedThumbnailUrl}
          controls
          preload="auto"
          style={{ width: '100%', maxHeight: '400px' }}
          onPlay={(e) => trackPlayback('play', e.target.currentTime)}
          onPause={(e) => trackPlayback('pause', e.target.currentTime)}
          onEnded={(e) => trackPlayback('ended', e.target.currentTime)}
          onSeeked={(e) => trackPlayback('seeked', e.target.currentTime)}
          onError={(e) => handleMediaError(e, 'Video')}
          onLoadStart={() => console.log('✅ Video loading started:', authenticatedUrl)}
          onLoadedMetadata={(e) => {
            console.log('✅ Video metadata loaded');
            console.log('Duration:', e.target.duration);
            console.log('Video dimensions:', e.target.videoWidth, 'x', e.target.videoHeight);
            console.log('Format:', e.target.currentSrc);
          }}
          onLoadedData={() => console.log('✅ Video data loaded (first frame ready)')}
          onCanPlay={() => console.log('✅ Video can play (enough data buffered)')}
          onCanPlayThrough={() => console.log('✅ Video can play through (all data buffered)')}
          onStalled={() => console.error('⚠️ Video stalled (network issue?)')}
          onSuspend={() => console.warn('⏸️ Video suspended (browser stopped fetching)')}
          onAbort={() => console.error('❌ Video aborted')}
          onEmptied={() => console.error('❌ Video emptied')}
          onWaiting={() => console.log('⏳ Video waiting for more data...')}
          onProgress={(e) => {
            if (e.target.buffered.length > 0) {
              const buffered = e.target.buffered.end(0);
              const duration = e.target.duration;
              console.log(`📊 Buffering: ${(buffered / duration * 100).toFixed(1)}%`);
            }
          }}
        />
        <TranscriptionDisplay />
      </div>
    );
  }

  // Audio playback with equalizer
  return (
    <div>
      <div className="audio-playback-container">
        <div className={`equalizer-container ${isPlaying ? 'playing' : ''}`}>
          <div className="equalizer-bar"></div>
          <div className="equalizer-bar"></div>
          <div className="equalizer-bar"></div>
          <div className="equalizer-bar"></div>
          <div className="equalizer-bar"></div>
        </div>
        <audio
          ref={mediaRef}
          src={authenticatedUrl}
          controls
          preload="auto"
          style={{ width: '100%' }}
          onPlay={(e) => {
            onAudioStateChange && onAudioStateChange(true);
            trackPlayback('play', e.target.currentTime);
          }}
          onPause={(e) => {
            onAudioStateChange && onAudioStateChange(false);
            trackPlayback('pause', e.target.currentTime);
          }}
          onEnded={(e) => {
            onAudioStateChange && onAudioStateChange(false);
            trackPlayback('ended', e.target.currentTime);
          }}
          onSeeked={(e) => trackPlayback('seeked', e.target.currentTime)}
          onError={(e) => handleMediaError(e, 'Audio')}
          onLoadStart={() => console.log('Audio loading started:', authenticatedUrl)}
          onCanPlay={() => console.log('Audio can play:', authenticatedUrl)}
        />
      </div>
      <TranscriptionDisplay />
    </div>
  );
}

export default PlaybackSection;
